import type { ReactNode } from 'react';

import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import {
  CardBadges,
  CardHoverImage,
  CardQuickAdd,
  CardSwatches,
} from '@/features/storefront/components/ProductCardExtras';
import { useProductCardSettings } from '@/features/storefront/components/productCardSettings';
import { useShopContent } from '@/features/storefront/content/useContent';
import { RatingSummary } from '@/features/storefront/components/StarRating';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { focalStyle } from '@/features/storefront/focal';
import { STOREFRONT_MONO_STYLE, STOREFRONT_TILE_IMAGE } from '@/features/storefront/theme';
import { CardWishlistSlot } from '@/features/storefront/wishlist/WishlistButton';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * How a product card presents itself, chosen by the layout family or a section.
 *
 * - `standard`: square image, name and price beneath - today's card.
 * - `framed`: the same square picture, but the whole tile is one bordered box
 *   with the name and price in a caption band under the picture. The catalog
 *   listing wears this in every family.
 * - `airy`: taller image, quieter type and more room. Editorial and minimal.
 * - `overlay`: name and price sit on the image itself, heavy and uppercase.
 * - `panel`: one solid tile in the surface colour, the picture on top and a
 *   roomy caption under it - name, price, stars and how many colours. A home
 *   page's product slider wears it.
 */
export type CardPresentation = 'standard' | 'framed' | 'airy' | 'overlay' | 'panel';

/**
 * What sits on a card's picture besides the picture: the theme's badges
 * (top-left), the quick add (bottom, or top on the overlay card whose caption
 * owns the bottom) and the actions slot (top-right).
 *
 * ACTIONS SLOT - `actions` renders in the picture's top-right corner, above the
 * hover photo, in a wrapper that already stops a click from following the
 * card's link. It is where per-product buttons such as the wishlist heart go:
 * pass one through `<ProductCard actions={...} />`, or render it here next to
 * `{actions}` so every card in the shop gets it without each caller passing it.
 */
function CardMediaLayer({
  product,
  actions,
  quickAddPlacement = 'bottom',
}: {
  product: StorefrontProductSummary;
  actions?: ReactNode;
  quickAddPlacement?: 'bottom' | 'top';
}) {
  return (
    <>
      <CardBadges product={product} />
      {actions ? (
        <div
          className="absolute right-3 top-3 z-10 flex flex-col items-end gap-2"
          data-card-actions
          onClick={(event) => {
            // Inside the card's link: a button here acts, it does not navigate.
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {actions}
        </div>
      ) : (
        // The wishlist plugin's heart, in the same corner, when it asks for one.
        <CardWishlistSlot productId={product.id} />
      )}
      <CardQuickAdd product={product} placement={quickAddPlacement} />
    </>
  );
}

/**
 * One product in a grid: the picture, the name, the price, the truth about
 * stock. Just the card's body - the caller wraps it in its own link (a router
 * link on the listing, an inert preview link in the customizer) with the
 * `group` class, which is what the image's hover zoom listens for.
 */
export function ProductCard({
  product,
  presentation = 'standard',
  sizes,
  actions,
}: {
  product: StorefrontProductSummary;
  presentation?: CardPresentation;
  /**
   * Per-product buttons on the picture's top-right corner (the wishlist
   * heart). See `CardMediaLayer` for the slot.
   */
  actions?: ReactNode;
  /**
   * How wide this card's slot is, for picking a rendition. The card cannot work
   * it out for itself - the grid above it owns the column count - so a caller
   * that knows passes `gridImageSizes(columns)`. Without it the browser falls
   * back to the widest rung, which is correct but heavier than it needs to be.
   */
  sizes?: string;
}) {
  const copy = useShopContent();
  const outOfStock = copy('product_card.out_of_stock');
  const { cards } = useProductCardSettings();
  // With swatches on, the dots say how many colours there are; the panel
  // card's "6 colours" line would only repeat them.
  const showColourCount = !(cards.swatches && (product.colours?.length ?? 0) > 0);
  // Only a theme whose tokens ask for them (`cardRatings`) shows stars on its
  // cards - a CSS switch rather than a lookup, so the card needs nothing but
  // the theme's variables around it. Elsewhere the product page shows them.
  const stars = (className: string) => (
    <div style={{ display: 'var(--storefront-card-ratings, none)' }}>
      <RatingSummary average={product.rating_average} count={product.rating_count} className={className} />
    </div>
  );

  if (presentation === 'overlay') {
    return (
      <div
        className="relative aspect-square w-full overflow-hidden"
        style={{
          backgroundColor: 'var(--storefront-surface)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        {product.image_url ? (
          // The overlay card's name and price sit on the picture, so here the
          // picture is a background and has to fill the card - see
          // STOREFRONT_TILE_IMAGE for why every other tile fits instead.
          // Filling means cropping, so the photo is held at the merchant's
          // chosen point; the card clips a zoom, and the hover's `scale`
          // composes with it rather than replacing it.
          <StorefrontImage
            url={product.image_url}
            sizes={sizes}
            alt={product.name}
            className="size-full object-cover transition-transform group-hover:scale-105"
            style={focalStyle(product.image_focal)}
          />
        ) : null}
        <CardHoverImage product={product} sizes={sizes} className="object-cover" />
        <CardMediaLayer product={product} actions={actions} quickAddPlacement="top" />
        <div
          className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-10 text-white"
          style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.65))' }}
        >
          <h3 className="text-sm font-bold uppercase tracking-wide">{product.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm" style={STOREFRONT_MONO_STYLE}>
            <span className="font-semibold">{formatMoney(product.price)}</span>
            {product.compare_at_price ? (
              <span className="line-through opacity-70">
                {formatMoney(product.compare_at_price)}
              </span>
            ) : null}
            {!product.in_stock ? <span className="opacity-80">· {outOfStock}</span> : null}
          </div>
          {stars('mt-1 [&_span]:!text-white/80')}
          <CardSwatches product={product} className="text-white" />
        </div>
      </div>
    );
  }

  if (presentation === 'panel') {
    return (
      <div
        className="flex h-full flex-col overflow-hidden"
        style={{
          backgroundColor: 'var(--storefront-surface)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        <div className="relative aspect-square w-full overflow-hidden">
          {product.image_url ? (
            <StorefrontImage
              url={product.image_url}
              sizes={sizes}
              alt={product.name}
              className={cn(
                'size-full',
                STOREFRONT_TILE_IMAGE,
                'transition-transform group-hover:scale-105',
              )}
            />
          ) : null}
          <CardHoverImage product={product} sizes={sizes} className={STOREFRONT_TILE_IMAGE} />
          <CardMediaLayer product={product} actions={actions} />
        </div>
        <div className="flex-1 px-5 pb-6 pt-5">
          <h3 className="text-base font-semibold">{product.name}</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm" style={STOREFRONT_MONO_STYLE}>
            <span>{formatMoney(product.price)}</span>
            {product.compare_at_price ? (
              <span className="line-through" style={{ color: 'var(--storefront-muted)' }}>
                {formatMoney(product.compare_at_price)}
              </span>
            ) : null}
          </div>
          {stars('mt-2')}
          <CardSwatches product={product} />
          {showColourCount && (product.colour_count ?? 0) > 0 ? (
            <p className="mt-2 text-xs">
              {copy(
                product.colour_count === 1
                  ? 'product_card.colour_count_single'
                  : 'product_card.colour_count',
                { count: product.colour_count ?? 0 },
              )}
            </p>
          ) : null}
          {!product.in_stock ? (
            <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
              {outOfStock}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (presentation === 'framed') {
    return (
      <div
        className="flex h-full flex-col overflow-hidden border"
        style={{
          borderRadius: 'var(--storefront-radius)',
          borderColor: 'var(--storefront-surface)',
        }}
      >
        <div
          className="relative aspect-square w-full overflow-hidden"
          style={{ backgroundColor: 'var(--storefront-surface)' }}
        >
          {product.image_url ? (
            <StorefrontImage
              url={product.image_url}
              sizes={sizes}
              alt={product.name}
              className={cn(
                'size-full',
                STOREFRONT_TILE_IMAGE,
                'transition-transform group-hover:scale-105',
              )}
            />
          ) : null}
          <CardHoverImage product={product} sizes={sizes} className={STOREFRONT_TILE_IMAGE} />
          <CardMediaLayer product={product} actions={actions} />
        </div>
        {/* The caption sits inside the box, on the page's own colour, so it
            reads as a label under the picture rather than as loose text. */}
        <div
          className="flex-1 px-4 py-3.5"
          style={{ backgroundColor: 'var(--storefront-background)' }}
        >
          <h3 className="text-sm font-medium">{product.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm" style={STOREFRONT_MONO_STYLE}>
            <span>{formatMoney(product.price)}</span>
            {product.compare_at_price ? (
              <span className="line-through" style={{ color: 'var(--storefront-muted)' }}>
                {formatMoney(product.compare_at_price)}
              </span>
            ) : null}
          </div>
          {stars('mt-1')}
          <CardSwatches product={product} />
          {!product.in_stock ? (
            <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
              {outOfStock}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  const airy = presentation === 'airy';
  return (
    <div>
      <div
        className={cn('relative w-full overflow-hidden', airy ? 'aspect-[3/4]' : 'aspect-square')}
        style={{
          backgroundColor: 'var(--storefront-surface)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        {product.image_url ? (
          <StorefrontImage
            url={product.image_url}
            sizes={sizes}
            alt={product.name}
            className={cn(
              'size-full',
              STOREFRONT_TILE_IMAGE,
              'transition-transform group-hover:scale-105',
            )}
          />
        ) : null}
        <CardHoverImage product={product} sizes={sizes} className={STOREFRONT_TILE_IMAGE} />
        <CardMediaLayer product={product} actions={actions} />
      </div>
      <h3 className={cn('text-sm', airy ? 'mt-4 font-normal' : 'mt-3 font-medium')}>
        {product.name}
      </h3>
      <div
        className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm', airy ? 'mt-1.5' : 'mt-1')}
        style={STOREFRONT_MONO_STYLE}
      >
        <span className={airy ? undefined : 'font-semibold'}>{formatMoney(product.price)}</span>
        {product.compare_at_price ? (
          <span className="line-through" style={{ color: 'var(--storefront-muted)' }}>
            {formatMoney(product.compare_at_price)}
          </span>
        ) : null}
      </div>
      {stars(airy ? 'mt-1.5' : 'mt-1')}
      <CardSwatches product={product} />
      {!product.in_stock ? (
        <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {outOfStock}
        </p>
      ) : null}
    </div>
  );
}
